import { DestinationEvent } from '@eventbrite/event-renderer';
import { FeatureFlags } from '@eventbrite/feature-flag-utils';

import React, { useState } from 'react';
import { ThingsToDoSection } from '../../components/ThingsToDoSection';
import { FAQSection } from '../../containers/FAQSection';
import { HeroSection, HeroSectionProps } from '../../containers/HeroSection';
import { NearbyCities, NearbyCitiesProps } from '../../containers/NearbyCities';
import {
    Breadcrumb,
    ThingsToDoBreadcrumbs,
} from '../../containers/ThingsToDoBreadcrumbs';
import {
    TopActivities,
    TopActivitiesProps,
} from '../../containers/TopActivities';
import {
    TrendingSearches,
    TrendingSearchesProps,
} from '../../containers/TrendingSearches';
import {
    CollectionShape,
    UniqueExperiences,
} from '../../containers/UniqueExperiences';
import { Category, WaysToTour } from '../../containers/WaysToTour';

import { HAS_WINDOW } from '@eventbrite/feature-detection';
import {
    CORE_SEO_API_KEY_CONFIG,
    useFeatureFlagState,
} from '@eventbrite/splitio-checker';
import { BrowseByDate } from '../../components/BrowseByDate';
import { CategoryFilterBar } from '../../components/CategoryFilterBar';
import { categoryToIconMap } from '../../components/CategoryFilterBar/categoryToIconMap';
import {
    useGetSelectedCategory,
    useReactQueryEnabler,
    useSingleToggle,
} from '../../components/CategoryFilterBar/hooks';
import { DateOption, DATE_OPTIONS } from '../../components/DateFilterDropdown';
import { Organizer } from '../../components/OrganizersSection/utils';
import { TopOrganizers } from '../../containers/TopOrganizers';
import { useQuerySearchService } from '../../hooks/eventHooks';
import { LocationData } from '../../types/AppProps';
import { userOperatingSystemIs } from '../../utils';
import {
    BentoCarouselSection,
    ORDERED_TTD_CATEGORIES,
} from '../point-of-interest/containers/BentoCarouselSection';
import { CategoryCarousel } from '../point-of-interest/containers/CategoryCarousel';

interface ThingsToDoProps {
    breadcrumbs: Breadcrumb[];
    trendingSearchesResult: TrendingSearchesProps;
    topEventsData: TopActivitiesProps;
    nearbyEvents?: Array<DestinationEvent>;
    nearbyFreeEvents?: Array<DestinationEvent>;
    nearbyNightlifeEvents?: Array<DestinationEvent>;
    nearbyCitiesResult?: NearbyCitiesProps;
    topOrganizers: Organizer[];
    categories?: Category[];
    heroSection: HeroSectionProps;
    editorialCollections: CollectionShape[];
    featureFlags: FeatureFlags;
    query_location_data: LocationData;
    userId: string | undefined;
    place_id: string;
    location_slug: string;
    language_code: string;
}

export const ThingsToDoPage: React.FunctionComponent<ThingsToDoProps> = (
    props,
) => {
    const IS_IOS_USER = userOperatingSystemIs('iOS');
    const enableTTDBentoCarouselExperience = useFeatureFlagState(
        'enable_ttd_bento_carousel_experience',
        CORE_SEO_API_KEY_CONFIG,
    );
    const enabledTTDCategoryFilter =
        useFeatureFlagState(
            'enable_ttd_category_filter',
            CORE_SEO_API_KEY_CONFIG,
            { userId: props.userId ?? '' },
        ) && !IS_IOS_USER;
    const enabledTTDDateFilter =
        useFeatureFlagState('enable_ttd_date_filter', CORE_SEO_API_KEY_CONFIG, {
            userId: props.userId ?? '',
        }) && !IS_IOS_USER;

    const { shouldEnableQuery, enableQuery } = useReactQueryEnabler();
    const { setCategoryTag } = useGetSelectedCategory();
    const { handleToggled, selectedCategory, resetFiltersRef } =
        useSingleToggle({
            categories: categoryToIconMap,
        });
    const [numCarousels, setNumCarousels] = useState(5);
    const [dateSelection, changeDateSelection] = useState<DateOption>(
        DATE_OPTIONS[0],
    );

    const getIsOnline = () => {
        if (HAS_WINDOW) {
            return window.location.href.includes('online');
        }
        return false;
    };

    const { data, isLoading } = useQuerySearchService({
        queryOverload: {
            page_size: 100,
            include_manual_events: true,
            include_mature_events: true,
            dates: [dateSelection.searchValue],
            page_type: 'TTD',
            tags: [selectedCategory?.tagSelector ?? ''],
            online_events_only: getIsOnline(),
        },
        location: {
            latitude: Number(props.query_location_data.latitude),
            longitude: Number(props.query_location_data.longitude),
            isOnline: props.query_location_data.isOnline ?? getIsOnline(),
            placeId: props.place_id,
        },
        enabled: shouldEnableQuery,
        retryOnMount: false,
    });

    const RenderEvents = () => {
        if (enabledTTDCategoryFilter && shouldEnableQuery) {
            return (
                <>
                    <TopActivities
                        isLoading={isLoading}
                        {...(data?.topActivities ?? {
                            top_events: [],
                            num_online_events: 0,
                            num_physical_events: 0,
                        })}
                    />
                    <CategoryCarousel
                        category_events={data?.category_events}
                        isLoading={isLoading}
                        populatedCategoriesCount={
                            data?.carouselCount || numCarousels
                        }
                        setNumCarousels={setNumCarousels}
                        locationData={{
                            latitude: Number(
                                props.query_location_data.latitude,
                            ),
                            longitude: Number(
                                props.query_location_data.longitude,
                            ),
                            isOnline:
                                props.query_location_data.isOnline ??
                                getIsOnline(),
                        }}
                        pageType="TTD"
                        handleToggle={handleToggled}
                        changeDateSelection={changeDateSelection}
                    />
                </>
            );
        } else if (enableTTDBentoCarouselExperience) {
            return (
                <>
                    <TopActivities {...props.topEventsData} />
                    <BentoCarouselSection
                        orderedCategories={ORDERED_TTD_CATEGORIES}
                        nearbyEvents={props.nearbyEvents || []}
                        nearbyFreeEvents={props.nearbyFreeEvents || []}
                        nearbyNightlifeEvents={
                            props.nearbyNightlifeEvents || []
                        }
                        location_slug={props.location_slug}
                    />
                </>
            );
        }
        return <TopActivities {...props.topEventsData} />;
    };

    return (
        <>
            <ThingsToDoBreadcrumbs breadcrumbs={props.breadcrumbs} />
            <HeroSection
                placeImage={props.heroSection.placeImage}
                isPageArt={props.heroSection.isPageArt}
                artistName={props.heroSection.artistName}
                artistInstagramLink={props.heroSection.artistInstagramLink}
            />
            {enabledTTDCategoryFilter ? (
                <CategoryFilterBar
                    categories={categoryToIconMap}
                    pageType="TTD"
                    locationData={{
                        latitude: Number(
                            props.query_location_data?.latitude ?? 0,
                        ),
                        longitude: Number(
                            props.query_location_data.longitude ?? 0,
                        ),
                        isOnline: props.query_location_data?.isOnline ?? false,
                    }}
                    enableQuery={enableQuery}
                    dateSelection={dateSelection}
                    changeDateSelection={changeDateSelection}
                    setCategoryTag={setCategoryTag}
                    handleToggled={handleToggled}
                    selectedCategory={selectedCategory}
                    enableDateFilter={enabledTTDDateFilter}
                    ref={resetFiltersRef}
                />
            ) : null}
            <RenderEvents />
            <WaysToTour categories={props.categories} />
            <UniqueExperiences
                editorialCollections={props.editorialCollections}
            />
            {props.topOrganizers?.length ? (
                <TopOrganizers topOrganizers={props.topOrganizers} />
            ) : (
                <></>
            )}
            <TrendingSearches {...props.trendingSearchesResult} />
            {props.nearbyCitiesResult ? (
                <ThingsToDoSection>
                    <NearbyCities places={props.nearbyCitiesResult.places} />
                </ThingsToDoSection>
            ) : (
                <></>
            )}
            <FAQSection
                topEventsBucket={props.topEventsData.top_events || []}
                topOrganizersBucket={props?.topOrganizers || []}
                nearbyCitiesBucket={props.nearbyCitiesResult?.places || []}
                trendingSearchesBucket={
                    props.trendingSearchesResult.trending || []
                }
            />
            <BrowseByDate
                location_slug={props.location_slug}
                language_code={props.language_code}
            />
        </>
    );
};
